<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import type { MegaMenuItem } from '~~/shared/types/navigation'

/**
 * Accordion section for mobile menu
 * Expands to show product groups and action links
 * Updated to work with Hook/Products/Actions structure
 *
 * Links with href pattern `/contact?need=xxx` will trigger the Intent Capture Modal
 * instead of navigating, providing a smoother experience for high-intent users.
 */

defineProps<{
  item: MegaMenuItem
  isOpen: boolean
}>()

const emit = defineEmits<{
  toggle: []
  linkClick: []
}>()

const { isIntentLink, handleLink } = useIntentModal()

/**
 * Handle intent link click - opens modal and closes menu
 */
function onIntentClick(href: string) {
  handleLink(href)
  emit('linkClick')
}

/**
 * Handle regular link click - just closes menu
 */
function onRegularLinkClick() {
  emit('linkClick')
}
</script>

<template>
  <div class="border-b border-neutral-100">
    <!-- Section header (accordion trigger) -->
    <button
      type="button"
      class="flex w-full items-center justify-between px-4 py-4 text-left"
      :aria-expanded="isOpen"
      @click="emit('toggle')"
    >
      <span class="text-base font-semibold text-slate-900">
        {{ item.label }}
      </span>
      <ChevronDown
        class="h-5 w-5 text-neutral-400 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        aria-hidden="true"
      />
    </button>

    <!-- Expandable content -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[2000px] opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="max-h-[2000px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div v-if="isOpen" class="overflow-hidden bg-neutral-50 pb-4">
        <!-- Hook description (brief context) -->
        <p class="px-4 py-2 text-sm text-neutral-600">
          {{ item.hook.description }}
        </p>

        <!-- Product groups -->
        <div
          v-for="group in item.products"
          :key="group.id"
          class="mt-2"
        >
          <!-- Group title (if provided) -->
          <h4
            v-if="group.title"
            class="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-neutral-400"
          >
            {{ group.title }}
          </h4>

          <!-- Product links -->
          <ul>
            <li v-for="link in group.items" :key="link.href">
              <NuxtLink
                :to="link.href"
                class="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-100"
                @click="emit('linkClick')"
              >
                <Icon
                  v-if="link.icon"
                  :name="link.icon"
                  size="18"
                  class="shrink-0 text-primary-500"
                  aria-hidden="true"
                />
                <div class="min-w-0">
                  <span class="block text-sm font-medium text-slate-800">
                    {{ link.label }}
                  </span>
                  <span
                    v-if="link.description"
                    class="block text-xs text-neutral-500"
                  >
                    {{ link.description }}
                  </span>
                </div>
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Action links -->
        <div class="mt-4 border-t border-neutral-200 pt-4">
          <h4 class="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-primary-500">
            {{ item.actions.title }}
          </h4>
          <ul>
            <li v-for="link in item.actions.links" :key="link.href">
              <!-- Intent links: use button to prevent navigation -->
              <button
                v-if="isIntentLink(link.href)"
                type="button"
                class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-neutral-100 hover:text-primary-500"
                @click="onIntentClick(link.href)"
              >
                <Icon
                  name="lucide:arrow-right"
                  size="14"
                  class="text-primary-500"
                  aria-hidden="true"
                />
                {{ link.label }}
              </button>
              <!-- Regular links: use NuxtLink for navigation -->
              <NuxtLink
                v-else
                :to="link.href"
                class="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-neutral-100 hover:text-primary-500"
                @click="onRegularLinkClick"
              >
                <Icon
                  name="lucide:arrow-right"
                  size="14"
                  class="text-primary-500"
                  aria-hidden="true"
                />
                {{ link.label }}
              </NuxtLink>
            </li>
          </ul>

          <!-- Featured action (if present) -->
          <NuxtLink
            v-if="item.actions.featured"
            :to="item.actions.featured.href"
            class="mx-4 mt-3 flex items-center justify-between rounded-lg bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-100"
            @click="emit('linkClick')"
          >
            <span>{{ item.actions.featured.label }}</span>
            <Icon
              name="lucide:arrow-right"
              size="16"
              aria-hidden="true"
            />
          </NuxtLink>
        </div>
      </div>
    </Transition>
  </div>
</template>
